import React, { useState, useEffect } from 'react';
import { 
  Select, 
  Table, 
  Card, 
  Typography, 
  Space, 
  Tag, 
  Button, 
  Spin, 
  Alert,
  Empty,
  Tooltip,
  Tabs
} from 'antd';
import { 
  FileTextOutlined, 
  CalendarOutlined, 
  BankOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  RiseOutlined,
  FallOutlined
} from '@ant-design/icons';
import apiClient from '../../services/apiClient';
import { useNavigate } from 'react-router-dom';
import LandingLayout from './LandingLayout';
import './Announcements.css';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

const Announcements = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('ALL');
  const [issuers, setIssuers] = useState([]);
  const [selectedIssuer, setSelectedIssuer] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingIssuers, setLoadingIssuers] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalAnnouncements, setTotalAnnouncements] = useState(0);
  const [error, setError] = useState(null);

  // Base URL from environment variable
  const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:8080/api/v1/sb';

  const ANNOUNCEMENT_TYPE_LABELS = {
    FINANCIAL_ISIN: 'Financial ISIN',
    FINANCIAL_ISSUER: 'Financial Issuer',
    NON_FINANCIAL_BUSINESS: 'Non-Financial Business',
    NON_FINANCIAL_OTHER: 'Non-Financial Other',
  };

  const ANNOUNCEMENT_TYPE_COLORS = {
    FINANCIAL_ISIN: 'blue',
    FINANCIAL_ISSUER: 'green',
    NON_FINANCIAL_BUSINESS: 'orange',
    NON_FINANCIAL_OTHER: 'purple',
  };

  // Valid announcement types
  const ANNOUNCEMENT_TYPES = {
    ALL: 'ALL',
    FINANCIAL_ISIN: 'FINANCIAL_ISIN',
    FINANCIAL_ISSUER: 'FINANCIAL_ISSUER',
    NON_FINANCIAL_BUSINESS: 'NON_FINANCIAL_BUSINESS',
    NON_FINANCIAL_OTHER: 'NON_FINANCIAL_OTHER'
  };

  // Fetch all issuers and announcements on component mount
  useEffect(() => {
    fetchIssuers();
    fetchAllAnnouncements(1, pageSize, activeTab); // Fetch all announcements initially
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch announcements when tab changes
  useEffect(() => {
    setSelectedIssuer(null); // Clear issuer selection when tab changes
    setCurrentPage(1);
    setAnnouncements([]);
    setTotalAnnouncements(0);
    setError(null);
    fetchAllAnnouncements(1, pageSize, activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchIssuers = async () => {
    try {
      setLoadingIssuers(true);
      const response = await apiClient.get(`${baseUrl}/master-issuer/issuers/names-and-cin`);
      
      if (response.data && response.data.data) {
        setIssuers(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching issuers:', err);
      setError('Failed to load issuers. Please try again later.');
    } finally {
      setLoadingIssuers(false);
    }
  };

  const fetchAllAnnouncements = async (page = 1, size = 20, type = 'ALL') => {
    try {
      setLoading(true);
      setError(null);
      
      let endpoint;
      let params = { page: page - 1, size }; // Backend uses 0-indexed pages
      
      if (type === 'ALL') {
        // Get all announcements regardless of type
        endpoint = `${baseUrl}/master-issuer/announcements/all`;
      } else {
        // Get announcements by specific type
        endpoint = `${baseUrl}/master-issuer/announcements`;
        params.type = type;
      }
      
      const response = await apiClient.get(endpoint, { params });

      if (response.data && response.data.data) {
        const data = response.data.data;
        setAnnouncements(data.content || data.announcements || []);
        setTotalAnnouncements(data.totalElements ?? data.totalAnnouncements ?? 0);
      }
    } catch (err) {
      console.error(`Error fetching announcements:`, err);
      if (err.response && err.response.status === 204) {
        setAnnouncements([]);
        setTotalAnnouncements(0);
        setError(`No announcements found for ${type === 'ALL' ? 'all types' : type}.`);
      } else {
        setError(`Failed to load announcements. Please try again later.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAnnouncements = async (issuerName, page = 1, size = 20, type = 'ALL') => {
    try {
      setLoading(true);
      setError(null);

      const params = { page: page - 1, size };
      const endpoint =
        type === 'ALL'
          ? `${baseUrl}/master-issuer/${encodeURIComponent(issuerName)}/announcements/all`
          : `${baseUrl}/master-issuer/${encodeURIComponent(issuerName)}/announcements`;

      if (type !== 'ALL') {
        params.type = type;
      }

      const response = await apiClient.get(endpoint, { params });

      if (response.data && response.data.data) {
        const data = response.data.data;
        setAnnouncements(data.content || data.announcements || []);
        setTotalAnnouncements(data.totalElements ?? data.totalAnnouncements ?? 0);
      }
    } catch (err) {
      console.error(`Error fetching announcements:`, err);
      if (err.response && err.response.status === 204) {
        setAnnouncements([]);
        setTotalAnnouncements(0);
        setError(`No ${type === 'ALL' ? '' : type + ' '}announcements found for this issuer.`);
      } else {
        setError(`Failed to load announcements. Please try again later.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
  };

  const handleIssuerChange = (value) => {
    setSelectedIssuer(value);
    setCurrentPage(1);

    setAnnouncements([]);
    setTotalAnnouncements(0);
    setError(null);

    if (value) {
      fetchAnnouncements(value, 1, pageSize, activeTab);
    } else {
      fetchAllAnnouncements(1, pageSize, activeTab);
    }
  };

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
    if (selectedIssuer) {
      fetchAnnouncements(selectedIssuer, pagination.current, pagination.pageSize, activeTab);
    } else {
      fetchAllAnnouncements(pagination.current, pagination.pageSize, activeTab);
    }
  };

  const handleIssuerClick = (record) => {
    // Use issuer name to navigate to issuer details
    if (record.issuer) {
      const identifier = encodeURIComponent(record.issuer);
      navigate(`/issuer-details/${identifier}`);
    }
  };

  const getTypeDisplayName = (type) => {
    const typeNames = {
      'ALL': 'All Announcements',
      'FINANCIAL_ISIN': 'Financial ISIN',
      'FINANCIAL_ISSUER': 'Financial Issuer',
      'NON_FINANCIAL_BUSINESS': 'Non-Financial Business',
      'NON_FINANCIAL_OTHER': 'Non-Financial Other'
    };
    return typeNames[type] || type;
  };

  // Render common tab content (filter, error, table)
  const renderTabContent = (type) => {
    const displayName = getTypeDisplayName(type);
    const loadingText = type === 'ALL' ? 'Loading all announcements...' : `Loading ${displayName} announcements...`;

    return (
      <>
        <Card className="announcements-filter-card">
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <div>
              <Text strong style={{ marginBottom: 8, display: 'block' }}>
                Select Issuer
              </Text>
              <Select
                showSearch
                allowClear
                placeholder="Search and select an issuer (or leave empty to view all)"
                style={{ width: '100%', maxWidth: 600 }}
                onChange={handleIssuerChange}
                value={selectedIssuer}
                loading={loadingIssuers}
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                size="large"
              >
                {issuers.map((issuer) => (
                  <Option key={issuer.issuer} value={issuer.issuer}>
                    {issuer.issuer}
                  </Option>
                ))}
              </Select>
            </div>
          </Space>
        </Card>

        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            closable
            style={{ marginBottom: 16 }}
          />
        )}

        {loading ? (
          <Card className="announcements-table-card">
            <div style={{ textAlign: 'center', padding: '50px 0', minWidth: '1200px' }}>
              <Spin size="large" />
              <Text style={{ display: 'block', marginTop: 16 }}>
                {loadingText}
              </Text>
            </div>
          </Card>
        ) : (
          <Card className="announcements-table-card">
            <Table
              columns={
                type === 'ALL'
                  ? [
                      ...columns.slice(0, 1),
                      {
                        title: 'Type',
                        dataIndex: 'announcementType',
                        key: 'announcementType',
                        width: 180,
                        render: (announcementType) => (
                          <Tag color={ANNOUNCEMENT_TYPE_COLORS[announcementType] || 'default'}>
                            {ANNOUNCEMENT_TYPE_LABELS[announcementType] || announcementType || 'N/A'}
                          </Tag>
                        ),
                      },
                      ...columns.slice(1),
                    ]
                  : columns
              }
              dataSource={announcements}
              rowKey={(record) => `${record.issuer}-${record.seqId}`}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalAnnouncements,
                showSizeChanger: true,
                showTotal: (total, range) => 
                  `${range[0]}-${range[1]} of ${total} ${displayName.toLowerCase()}`,
                pageSizeOptions: ['10', '20', '50', '100'],
              }}
              onChange={handleTableChange}
              scroll={{ x: 1200 }}
              locale={{
                emptyText: (
                  <Empty
                    description={selectedIssuer ? `No ${displayName.toLowerCase()} found for this issuer` : `No ${displayName.toLowerCase()} available`}
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                ),
              }}
            />
          </Card>
        )}
      </>
    );
  };

  const columns = [
    {
      title: 'Issuer',
      dataIndex: 'issuer',
      key: 'issuer',
      width: 250,
      render: (issuer, record) => (
        issuer ? (
          <Text 
            strong 
            style={{ color: '#1890ff', cursor: 'pointer' }}
            onClick={() => handleIssuerClick(record)}
          >
            {issuer}
          </Text>
        ) : (
          <Text strong>N/A</Text>
        )
      ),
    },
    {
      title: 'Subject',
      dataIndex: 'description',
      key: 'description',
      width: 300,
      render: (description) => (
        <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {description || 'No subject'}
        </Paragraph>
      ),
    },
    {
      title: 'Details',
      dataIndex: 'attachmentText',
      key: 'attachmentText',
      render: (attachmentText) => (
        <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {attachmentText || 'No details available'}
        </Paragraph>
      ),
    },
    {
      title: 'Attachment',
      key: 'attachment',
      width: 150,
      align: 'center',
      render: (_, record) => (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          {record.attachmentFile ? (
            <>
              <Button
                type="link"
                size="small"
                icon={<EyeOutlined />}
                href={record.attachmentFile}
                target="_blank"
                rel="noopener noreferrer"
                style={{ padding: 0 }}
              >
                View PDF
              </Button>
              
            </>
          ) : (
            <Text type="secondary">No file</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'announcementDate',
      key: 'announcementDate',
      width: 180,
      render: (date) => (
        <Space>
          <CalendarOutlined style={{ color: '#1890ff' }} />
          <Text>{date || 'N/A'}</Text>
        </Space>
      ),
    },
  ];

  return (
    <LandingLayout>
      <div className="announcements-container">
        <div className="announcements-header">
          <Title level={2}>
            <FileTextOutlined /> Corporate Announcements
          </Title>
          <Paragraph>
            View latest corporate announcements from NSE categorized by type
          </Paragraph>
        </div>

      <Tabs 
        activeKey={activeTab} 
        onChange={handleTabChange}
        size="large"
        style={{ marginBottom: 24 }}
      >
        <TabPane 
          tab={
            <span>
              <FileTextOutlined /> All Announcements
            </span>
          } 
          key="ALL"
        >
          {renderTabContent('ALL')}
        </TabPane>

        <TabPane 
          tab={
            <span>
              <BankOutlined /> Financial ISIN
            </span>
          } 
          key="FINANCIAL_ISIN"
        >
          {renderTabContent('FINANCIAL_ISIN')}
        </TabPane>

        <TabPane 
          tab={
            <span>
              <CheckCircleOutlined /> Financial Issuer
            </span>
          } 
          key="FINANCIAL_ISSUER"
        >
          {renderTabContent('FINANCIAL_ISSUER')}
        </TabPane>

        <TabPane 
          tab={
            <span>
              <RiseOutlined /> Non-Financial Business
            </span>
          } 
          key="NON_FINANCIAL_BUSINESS"
        >
          {renderTabContent('NON_FINANCIAL_BUSINESS')}
        </TabPane>

        <TabPane 
          tab={
            <span>
              <FallOutlined /> Non-Financial Other
            </span>
          } 
          key="NON_FINANCIAL_OTHER"
        >
          {renderTabContent('NON_FINANCIAL_OTHER')}
        </TabPane>
      </Tabs>
      </div>
    </LandingLayout>
  );
};

export default Announcements;
